/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/

export const API_DOC_IDS = [
  'kling-4.0',
  'veo-3-1',
  'veo-omni-flash',
  'veo-omni-flash-video-edit',
  'wan3-video',
  'minimax-h3',
  'seedance-official',
  'gemini-3-pro-image-preview',
  'gemini-3-1-flash-image-preview',
  'gpt-image-2',
  'gpt-image-2.5',
  'gpt-image-2.5-flare',
  'gpt-image-2.5-sunburst',
  'gpt-chat',
  'claude-messages',
] as const

export type ApiDocId = (typeof API_DOC_IDS)[number]

export type ApiDocDefinition = {
  id: ApiDocId
  models: string[]
  title: string
  summaryKey: string
  isAsync: boolean
  duration?: number
  resolutions?: string[]
  isResponses?: boolean
  isAnthropic?: boolean
  isImageGeneration?: boolean
  isGeminiImageGeneration?: boolean
  isSd25Video?: boolean
  isMegabyVideo?: boolean
  isWan3Video?: boolean
  isMiniMaxH3Video?: boolean
}

export const API_DOCUMENTS: ApiDocDefinition[] = [
  {
    id: 'kling-4.0',
    models: ['kling-4.0'],
    title: 'Kling 4.0',
    summaryKey:
      'Kling 4.0 supports 3-30 second videos, all aspect ratios, 720p and 1080p output, and mixed image, video and audio references.',
    isAsync: true,
    resolutions: ['720p', '1080p'],
  },
  {
    id: 'veo-3-1',
    models: ['veo-3-1'],
    title: 'VEO 3.1',
    summaryKey: '固定生成 8 秒视频；可使用首尾帧或多图参考。',
    isAsync: true,
    duration: 8,
    resolutions: ['720p', '1080p', '4k'],
  },
  {
    id: 'veo-omni-flash',
    models: ['veo-omni-flash'],
    title: 'VEO Omni Flash',
    summaryKey: '固定生成 10 秒视频；最多使用 6 张多图参考。',
    isAsync: true,
    duration: 10,
    resolutions: ['720p', '1080p', '4k'],
  },
  {
    id: 'veo-omni-flash-video-edit',
    models: ['veo-omni-flash-video-edit'],
    title: 'VEO Omni Flash Video Edit',
    summaryKey:
      '固定生成 10 秒视频；需提供视频参考，可额外使用最多 6 张多图参考。',
    isAsync: true,
    duration: 10,
    resolutions: ['720p', '1080p', '4k'],
  },
  {
    id: 'wan3-video',
    models: ['wan3.0-video'],
    title: 'Wan3.0 Video',
    summaryKey:
      'Asynchronous Wan3.0 video generation with 480P, 720P, or 1080P output. Supports 2-30 second videos and public image, video, and audio URL references. Pricing is ¥0.37/s for 480P, ¥0.43/s for 720P, and ¥0.48/s for 1080P; reference videos are billed by actual duration.',
    isAsync: true,
    isWan3Video: true,
  },
  {
    id: 'minimax-h3',
    models: ['minimax-h3'],
    title: 'MiniMax H3',
    summaryKey:
      'MiniMax H3 asynchronous video generation. Supports 4-15 second videos, 768P or 2K output, up to 9 images, 3 reference videos, and 3 reference audios.',
    isAsync: true,
    isMiniMaxH3Video: true,
  },
  {
    id: 'seedance-official',
    models: [
      'seedance2.0',
      'seedance2.0fast',
      'seedance2.0mini',
      'seedance2.5',
    ],
    title: 'Seedance 2.0 / Fast / Mini / 2.5',
    summaryKey:
      '官方内容生成任务协议：支持文生视频、图片、视频和音频参考；已接入 Canvas 画布。',
    isAsync: true,
  },
  {
    id: 'gemini-3-pro-image-preview',
    models: ['gemini-3-pro-image-preview'],
    title: 'Gemini 3 Pro Image Preview',
    summaryKey:
      'GoogleFlow-ultra image generation and editing through the Gemini generateContent API. Responses are synchronous; generated images may be returned as Base64 inlineData or hosted fileData.fileUri URLs.',
    isAsync: false,
    isGeminiImageGeneration: true,
  },
  {
    id: 'gemini-3-1-flash-image-preview',
    models: ['gemini-3.1-flash-image-preview'],
    title: 'Gemini 3.1 Flash Image Preview',
    summaryKey:
      'GoogleFlow-ultra image generation and editing through the Gemini generateContent API. Responses are synchronous; generated images may be returned as Base64 inlineData or hosted fileData.fileUri URLs.',
    isAsync: false,
    isGeminiImageGeneration: true,
  },
  {
    id: 'gpt-image-2',
    models: ['gpt-image-2'],
    title: 'GPT Image 2',
    summaryKey:
      'OpenAI-compatible image generation and editing. 1K is routed to vip; 2K and 4K are routed to svip.',
    isAsync: false,
    isImageGeneration: true,
  },
  {
    id: 'gpt-image-2.5',
    models: ['gpt-image-2.5'],
    title: 'GPT Image 2.5 (High Reasoning)',
    summaryKey:
      'OpenAI-compatible GPT Image 2.5 high-reasoning image generation and editing. This model accepts the same Images API format as GPT Image 2 and supports 1K output only.',
    isAsync: false,
    isImageGeneration: true,
  },
  {
    id: 'gpt-image-2.5-flare',
    models: ['gpt-image-2.5-flare'],
    title: 'GPT Image 2.5 Flare',
    summaryKey:
      'OpenAI-compatible GPT Image 2.5 Flare for fast, high-quality everyday image generation and editing. Supports 1K, 2K, and 4K output with auto, low, medium, high, xhigh, or max quality.',
    isAsync: false,
    isImageGeneration: true,
  },
  {
    id: 'gpt-image-2.5-sunburst',
    models: ['gpt-image-2.5-sunburst'],
    title: 'GPT Image 2.5 Sunburst',
    summaryKey:
      'OpenAI-compatible GPT Image 2.5 Sunburst for precise image generation and editing. Supports 1K, 2K, and 4K output with auto, low, medium, high, xhigh, or max quality.',
    isAsync: false,
    isImageGeneration: true,
  },
  {
    id: 'gpt-chat',
    models: [
      'gpt-5.6-sol',
      'gpt-5.6-terra',
      'gpt-5.6-luna',
      'gpt-5.5',
      'gpt-5.4-mini',
      'gpt-5.4',
    ],
    title: 'GPT Models',
    summaryKey: '可通过兼容 OpenAI 的 Responses API 调用以下 GPT 模型。',
    isAsync: false,
    isResponses: true,
  },
  {
    id: 'claude-messages',
    models: [
      'claude-fable-5',
      'claude-opus-4-6',
      'claude-opus-4-6-thinking',
      'claude-opus-4-7',
      'claude-opus-4-7-thinking',
      'claude-opus-4-8',
      'claude-opus-4-8-thinking',
      'claude-opus-5',
      'claude-opus-5-thinking',
      'claude-sonnet-4-5',
      'claude-sonnet-4-6',
      'claude-sonnet-4-6-thinking',
      'claude-sonnet-5',
      'claude-sonnet-5-thinking',
    ],
    title: 'Claude Models',
    summaryKey: '可通过 Anthropic 官方 Messages API 格式调用以下 Claude 模型。',
    isAsync: false,
    isAnthropic: true,
  },
]

export function findApiDocument(modelName: string): ApiDocDefinition | null {
  return (
    API_DOCUMENTS.find((document) => document.models.includes(modelName)) ??
    null
  )
}
